import { useEffect, useRef } from "react";

/**
 * Re-runs `fetcher` on a smart schedule so multiple users see fresh data
 * without manual reloads. Designed to be drop-in for any page that has
 * a `load()` function:
 *
 *   useEffect(() => { load(); }, [id]);
 *   useAutoRefresh(load);                      // default cadence
 *   useAutoRefresh(load, { intervalMs: 60_000 });
 *
 * Iter 173 (2026-02-20, Dirk) — quality-of-life refactor.
 * ════════════════════════════════════════════════════════
 * Pages were calling `setLoading(true)` on every 30s tick, blanking the
 * screen and resetting scroll while the user was reading. Two changes:
 *
 *   1. The fetcher is now invoked as `fetcher({ silent: true })` on
 *      background refreshes (the initial `load()` from useEffect still
 *      gets no argument — backwards-compatible). Pages opt in by
 *      reading the flag and skipping `setLoading(true)`:
 *
 *        const load = async (opts) => {
 *          const silent = opts?.silent;
 *          if (!silent) setLoading(true);
 *          ...
 *        };
 *
 *   2. Background refreshes are SUPPRESSED while the user is actively
 *      scrolling or interacting (touch/click/key in the last ~1.5s).
 *      As soon as the user pauses, the next scheduled tick runs.
 *      This means: scrolling a long JC list will NEVER trigger a
 *      mid-scroll re-render. Foreground tabs that the user has
 *      stopped touching for >1.5s still refresh every interval.
 *
 * Behaviour (unchanged externally):
 *   • Re-fetches every `intervalMs` while the tab is visible.
 *   • Pauses the timer when `document.visibilityState === "hidden"`.
 *   • Re-fetches once IMMEDIATELY when the tab regains focus.
 *   • Coalesces multiple events into one fetch.
 *   • Optional `enabled` flag.
 */

// Module-scope interaction tracker — shared across every page that uses
// useAutoRefresh. We track on `window` so background refreshes pause
// uniformly across nested components / parallel pages.
let _lastInteractionAt = 0;
function _installInteractionTracker() {
  if (typeof window === "undefined") return;
  if (window.__hv_interaction_tracker_installed) return;
  window.__hv_interaction_tracker_installed = true;
  const bump = () => { _lastInteractionAt = Date.now(); };
  // Passive listeners — must not block scrolling.
  const opts = { passive: true, capture: true };
  window.addEventListener("scroll", bump, opts);
  window.addEventListener("touchstart", bump, opts);
  window.addEventListener("touchmove", bump, opts);
  window.addEventListener("wheel", bump, opts);
  window.addEventListener("mousedown", bump, opts);
  window.addEventListener("keydown", bump, opts);
}
function _userIsBusy(thresholdMs = 1500) {
  return Date.now() - _lastInteractionAt < thresholdMs;
}

export function useAutoRefresh(fetcher, options = {}) {
  const {
    intervalMs = 30_000,         // sensible default — list pages
    refetchOnFocus = true,
    enabled = true,
    minGapMs = 4_000,            // dedupe window
    // 2026-02-20 — minimum quiet period after the user's last
    // interaction before a background refresh can run. Bumped to 1.5s
    // covers the typical scroll-momentum window on iOS Safari without
    // delaying refresh perceptibly.
    quietGapMs = 1_500,
  } = options;

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const lastRunAtRef = useRef(0);
  const inflightRef = useRef(false);

  useEffect(() => {
    _installInteractionTracker();
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const safeRun = async (reason) => {
      // Coalesce: if we just ran (or are still running), skip.
      const now = Date.now();
      if (inflightRef.current) return;
      if (now - lastRunAtRef.current < minGapMs) return;
      // Don't yank state out from under a scrolling/typing user.
      // Focus events get a pass — the user explicitly returned to the
      // tab and they expect freshness.
      if (reason !== "focus" && _userIsBusy(quietGapMs)) {
        // Try again shortly — if the user has paused by then, refresh.
        setTimeout(() => {
          if (!_userIsBusy(quietGapMs)) safeRun("retry");
        }, quietGapMs);
        return;
      }
      inflightRef.current = true;
      lastRunAtRef.current = now;
      try {
        // Pass { silent: true } so pages can skip their loading
        // skeleton and avoid blanking the screen. Legacy fetchers
        // that take no args still work — JS is lenient with extras.
        await fetcherRef.current?.({ silent: true, reason });
      } catch (_e) {
        // Swallow — caller's load() typically already surfaces errors.
        // Re-throwing here would crash the timer loop.
      } finally {
        inflightRef.current = false;
        lastRunAtRef.current = Date.now();
      }
    };

    let timerId = null;
    const startTimer = () => {
      if (timerId != null) return;
      timerId = setInterval(() => {
        if (document.visibilityState === "visible") safeRun("interval");
      }, intervalMs);
    };
    const stopTimer = () => {
      if (timerId != null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (refetchOnFocus) safeRun("focus");
        startTimer();
      } else {
        stopTimer();
      }
    };

    if (document.visibilityState === "visible") startTimer();
    document.addEventListener("visibilitychange", onVisibility);
    // Some mobile browsers fire `focus`/`pageshow` more reliably than
    // visibilitychange when returning from a deep background.
    if (refetchOnFocus) {
      window.addEventListener("focus", onVisibility);
      window.addEventListener("pageshow", onVisibility);
    }

    return () => {
      stopTimer();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
    };
  }, [enabled, intervalMs, refetchOnFocus, minGapMs, quietGapMs]);
}
